import React from "react";
import { View, StyleSheet } from "react-native";

import { Switch, Text, Divider } from "react-native-paper";

import { setThreadShown } from "../ariadne/thread";
import { useGameStore } from "../stores/useGameStoreFacade";

interface PathDisplayConfiguratorProps {
  compact?: boolean;
}

const PathDisplayConfigurator: React.FC<PathDisplayConfiguratorProps> = ({
  compact = false,
}) => {
  const pathDisplayMode = useGameStore((state) => state.pathDisplayMode);
  const setPathDisplayMode = useGameStore((state) => state.setPathDisplayMode);
  const gameStatus = useGameStore((state) => state.gameStatus);
  const aiPath = useGameStore((state) => state.aiPath);

  // Only show the gold path / Local Optimal route overlays once the game is over
  const showAdvancedOptions = gameStatus === "given_up" || gameStatus === "won";

  // Her thread (spec §5): offered for any game she played
  const showAiPathOption = aiPath.length > 0;

  const handleTogglePlayerPath = () => {
    setPathDisplayMode({ player: !pathDisplayMode.player });
  };

  // pathDisplayMode.optimal = gold path overlay; .suggested = Local Optimal
  // route overlay (persisted keys, Ruling R6).
  const handleToggleGoldPath = () => {
    setPathDisplayMode({ optimal: !pathDisplayMode.optimal });
  };

  const handleToggleLocalOptimalRoute = () => {
    setPathDisplayMode({ suggested: !pathDisplayMode.suggested });
  };

  const handleToggleAiPath = () => {
    void setThreadShown(!pathDisplayMode.ai);
  };

  if (compact) {
    // Compact version for small spaces (e.g. toolbar)
    return (
      <View style={styles.compactContainer}>
        <View style={styles.compactOption}>
          <Text style={styles.compactLabel}>Your Path</Text>
          <Switch
            value={pathDisplayMode.player}
            onValueChange={handleTogglePlayerPath}
          />
        </View>

        {showAdvancedOptions && (
          <>
            <View style={styles.compactOption}>
              <Text style={styles.compactLabel}>Global Optimal</Text>
              <Switch
                value={pathDisplayMode.optimal}
                onValueChange={handleToggleGoldPath}
              />
            </View>

            <View style={styles.compactOption}>
              <Text style={styles.compactLabel}>Local Optimal</Text>
              <Switch
                value={pathDisplayMode.suggested}
                onValueChange={handleToggleLocalOptimalRoute}
              />
            </View>
          </>
        )}

        {showAiPathOption && (
          <View style={styles.compactOption}>
            <Text style={styles.compactLabel}>Ariadne</Text>
            <Switch
              value={pathDisplayMode.ai}
              onValueChange={handleToggleAiPath}
            />
          </View>
        )}
      </View>
    );
  }

  // Standard version with labels
  return (
    <View style={styles.container}>
      <Text style={styles.title}>Path Display Options</Text>
      <Divider style={styles.divider} />

      <View style={styles.option}>
        <Text>Your Path</Text>
        <Switch
          value={pathDisplayMode.player}
          onValueChange={handleTogglePlayerPath}
        />
      </View>

      {showAdvancedOptions && (
        <>
          <View style={styles.option}>
            <Text>Gold Path</Text>
            <Switch
              value={pathDisplayMode.optimal}
              onValueChange={handleToggleGoldPath}
            />
          </View>

          <View style={styles.option}>
            <Text>Local Optimal Route</Text>
            <Switch
              value={pathDisplayMode.suggested}
              onValueChange={handleToggleLocalOptimalRoute}
            />
          </View>
        </>
      )}

      {showAiPathOption && (
        <View style={styles.option}>
          <Text>Ariadne</Text>
          <Switch
            value={pathDisplayMode.ai}
            onValueChange={handleToggleAiPath}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
    backgroundColor: "#fff",
    borderRadius: 8,
  },
  compactContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
    padding: 8,
  },
  compactOption: {
    flexDirection: "column",
    alignItems: "center",
    marginHorizontal: 4,
  },
  compactLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 8,
  },
  divider: {
    marginBottom: 12,
  },
  option: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginVertical: 8,
  },
});

export default PathDisplayConfigurator;
